<template>
    <div class="pb-10">
        <img
            v-if="payment?.provider?.iconUrl"
            :src="$filters.srcHost(payment.provider.iconUrl)"
            alt="logo"
            class="d-block mb-2"
            style="max-width: 300px"
        />
        <a v-if="payment?.provider?.merchantCabinetUrl" :href="payment.provider.merchantCabinetUrl" target="_blank">
            {{ $t('visit_site', {prop: payment.provider.name}) }}
        </a>
    </div>
    <v-form v-if="Object.keys(payment).length !== 0" ref="form" v-model="valid">
        <div class="pb-4">
            <v-number-input
                v-model="payment.markup"
                :min="0"
                hide-details="auto"
                :label="$t('markup_percent')"
                max-width="140"
                data-cy="markup-percent"
                :rules="[requiredRule('markup_percent')]"
            />
        </div>
        <div class="text-subtitle-1 font-weight-medium pb-3">{{ $t('charging') }}</div>
        <v-radio-group v-model="payment.guaranteePayment">
            <v-radio :label="$t('full_price')" value="FULL_PRICE" data-cy="full_price" />
            <v-radio
                :label="$t('maximal_possible_cancellation_fee')"
                value="MAX_CANCELLATION_FEE"
                data-cy="maximal_possible_cancellation_fee"
            />
            <v-radio
                :label="$t('minimal_possible_cancellation_fee')"
                value="MIN_CANCELLATION_FEE"
                data-cy="minimal_possible_cancellation_fee"
            />
        </v-radio-group>
        <template v-if="payment.refundSettings">
            <div class="pb-4">
                <div class="text-subtitle-1 font-weight-medium pb-3">{{ $t('refund') }}</div>
                <v-checkbox
                    v-model="payment.refundSettings.autoRefund"
                    :label="$t('auto_refund')"
                    data-cy="auto-refund"
                    hide-details
                />
                <div class="text-medium-emphasis">({{ $t('auto_refund_note') }})</div>
                <v-checkbox
                    v-model="payment.refundSettings.autoRefundOnModify"
                    :label="$t('auto_refund_on_modify')"
                    data-cy="auto-refund-on-modify"
                    hide-details
                />
                <div class="text-medium-emphasis">({{ $t('auto_refund_note') }})</div>
            </div>
        </template>
        <v-row no-gutters>
            <v-col cols="12" lg="8">
                <v-textarea v-model="payment.remark" :label="$t('remark_text_to_show_on_B2C')" data-cy="remark" />
            </v-col>
        </v-row>
        <template v-if="runtimeStore.getCRMOption('asp.option.enable.multiple.accounts.for.payment.gateway')">
            <div class="text-subtitle-1 font-weight-medium pb-3">{{ $t('accounts') }}</div>
            <v-radio-group v-model="payment.singleCredential">
                <v-row no-gutters class="ga-2">
                    <v-col class="flex-grow-0">
                        <v-radio
                            class="width-max-content"
                            :label="$t('one_account')"
                            :value="true"
                            data-cy="one_account"
                        />
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-radio
                            class="width-max-content"
                            :label="$t('multiple_accounts')"
                            :value="false"
                            data-cy="multiple_accounts"
                        />
                    </v-col>
                </v-row>
            </v-radio-group>
        </template>

        <template
            v-if="
                (payment.singleCredential &&
                    runtimeStore.getCRMOption('asp.option.enable.multiple.accounts.for.payment.gateway')) ||
                !runtimeStore.getCRMOption('asp.option.enable.multiple.accounts.for.payment.gateway')
            "
        >
            <OnlinePaymentsAccountForm
                ref="onlineSingleAccountRef"
                v-model="payment.multipleCredentials[defaultCredentialIndex(payment.multipleCredentials)]"
                :provider="payment.provider"
                :is-validated="payment.isValidated"
            />
        </template>
        <template v-else>
            <div class="full-width d-flex align-center pb-4">
                <v-btn data-cy="create-account" @click="createAccount">
                    {{ $t('create') }}
                </v-btn>
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
            <v-data-table
                :headers="headers"
                :items="multipleCredentialsItems(payment.multipleCredentials)"
                disable-sort
                density="compact"
                class="elevation-1 mb-2"
                :items-per-page="-1"
                hide-default-footer
            >
                <template v-slot:item.accountName="{item}">
                    <span
                        class="text-link"
                        :data-cy="`account-${item.accountName}`"
                        @click="selectMultipleCredential(item)"
                    >
                        {{ item.accountName || item.id }}
                    </span>
                    <div v-if="item.byDefault" class="text-medium-emphasis">({{ $t('by_default') }})</div>
                </template>
                <template v-slot:item.availabilityType="{item}">
                    {{ $t(`availabilityType.${item.availabilityType}`) }}
                </template>
                <template v-slot:item.isActive="{item}">
                    {{ item.isActive ? $t('active') : $t('inactive') }}
                </template>
            </v-data-table>
            <ModalWrapper v-model="isShowModal" max-width="1200" scrollable>
                <template v-slot:title>{{ $t('add_account') }}</template>
                <template v-slot:content>
                    <OnlinePaymentsAccountForm
                        ref="onlineAccountRef"
                        v-model="selectedMultipleCredential"
                        multiple-account
                        :provider="payment.provider"
                        :is-validated="payment.isValidated"
                    />
                </template>
                <template v-slot:btns>
                    <v-btn
                        :disabled="!onlineAccountRef?.valid"
                        variant="flat"
                        data-cy="add-account"
                        @click="addAccount"
                    >
                        {{ $t('apply') }}
                    </v-btn>
                </template>
            </ModalWrapper>
        </template>
        <v-row class="pa-4 pt-10 gc-4">
            <v-btn
                :disabled="isLoading || !valid || (payment.singleCredential && !onlineSingleAccountRef?.valid)"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn :disabled="isLoading" variant="text" data-cy="cancel" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'

    const props = defineProps<{
        selectedPSP: any
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const activeFilter = ref<boolean>(true)
    const isShowModal = ref<boolean>(false)
    const selectedMultipleCredential = ref(null)
    const payment = ref({})
    const onlineAccountRef = ref(null)
    const onlineSingleAccountRef = ref(null)

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
        payment.value = clone(props.selectedPSP)
    })

    const save = async (): Promise<void> => {
        isLoading.value = true
        try {
            await $api.pspSettings.put(payment.value.id, payment.value)
            $toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            $toast.error(e.msg || e?.[0]?.msg || e.errors?.[0]?.message)
        } finally {
            isLoading.value = false
        }
    }

    const headers = computed(() => {
        return [
            {title: t('name_of_account'), value: 'accountName', minWidth: '120px'},
            {title: t('currency_of_account'), value: 'baseCurrency'},
            {title: t('availability'), value: 'availabilityType'},
            {title: t('status'), value: 'isActive', minWidth: '120px', align: 'end'},
        ]
    })

    const multipleCredentialsItems = computed(() => items => {
        return items?.filter(({isActive}) => isActive === activeFilter.value)
    })

    const createAccount = () => {
        isShowModal.value = true

        selectedMultipleCredential.value = {
            isActive: true,
            accountName: null,
            byDefault: false,
            baseCurrency: null,
            serviceTypes: [],
            currencies: [],
            tourLeadCitizenshipId: null,
            credential: payment.value?.provider?.requiredCredentialsTemplate,
            availabilityType: 'FOR_ALL',
            salesChannels: {},
            directSales: false,
            availableForTO: false,
            availableForAgency: false,
            availableForCorporate: false,
            conditions: [],
        }
    }

    const addAccount = () => {
        if (selectedMultipleCredential.value.byDefault === true) {
            payment.value.multipleCredentials = payment.value.multipleCredentials.map(credential => ({
                ...credential,
                byDefault: false,
            }))
        }
        if (!selectedMultipleCredential.value?.id) {
            payment.value.multipleCredentials.push(selectedMultipleCredential.value)
        } else {
            payment.value.multipleCredentials = payment.value.multipleCredentials.map(credential =>
                credential.id === selectedMultipleCredential.value.id ? selectedMultipleCredential.value : credential
            )
        }
        isShowModal.value = false
    }

    const selectMultipleCredential = credential => {
        selectedMultipleCredential.value = clone(credential)
        isShowModal.value = true
    }

    const defaultCredentialIndex = computed(() => credentials => {
        return credentials?.findIndex(({byDefault}) => byDefault) || 0
    })
</script>
